import type { TargetReviewsResponse } from "@shared/types";
import { Lock, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ReviewSummarySection } from "@/components/ui/review-summary-section";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { resolveMediaUrl } from "@/lib/utils";
import { fetchDesignerReviews } from "@/services/reviews-api.service";
import {
	getPublicPortfolio,
	getPublicPortfolioProject,
	type PortfolioTemplate,
	type PublicPortfolioProjectResponse,
	type PublicPortfolioResponse,
} from "../services/portfolio-api.service";
import {
	PORTFOLIO_TEMPLATES,
	renderPublicTemplateHeader,
	renderPublicTemplateSection,
} from "../template-definitions";

function ProjectDetailsModal({
	data,
	onClose,
	onRequireAuth,
}: {
	data: PublicPortfolioProjectResponse;
	onClose: () => void;
	onRequireAuth: () => void;
}): React.ReactElement {
	const project = data.project;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
			<button
				type="button"
				aria-label="Close project details"
				className="absolute inset-0"
				onClick={onClose}
			/>
			{/* Modal panel */}
			<div
				role="dialog"
				aria-modal="true"
				style={{
					position: "relative",
					width: "100%",
					maxWidth: 760,
					maxHeight: "90vh",
					overflowY: "auto",
					background: "var(--color-dashboard-surface)",
					borderRadius: 14,
					boxShadow: "0 32px 80px rgba(30,41,59,0.32)",
				}}
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
			>
				{/* Header image with gradient overlay */}
				<div style={{ position: "relative" }}>
					<img
						src={resolveMediaUrl(project.thumbnailUrl)}
						alt={project.title}
						style={{
							width: "100%",
							maxHeight: 240,
							objectFit: "cover",
							borderRadius: "14px 14px 0 0",
						}}
					/>
					<div
						style={{
							position: "absolute",
							inset: 0,
							background:
								"linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.28) 50%, transparent 100%)",
							borderRadius: "14px 14px 0 0",
						}}
					/>
					{/* Close button */}
					<button
						type="button"
						onClick={onClose}
						style={{
							position: "absolute",
							top: 14,
							right: 14,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 34,
							height: 34,
							borderRadius: "50%",
							background: "rgba(30,41,59,0.55)",
							color: "var(--color-dashboard-on-fill)",
							border: "none",
							cursor: "pointer",
							backdropFilter: "blur(4px)",
						}}
					>
						<X className="h-4 w-4" />
					</button>
					{/* Title overlay */}
					<div
						style={{
							position: "absolute",
							bottom: 18,
							left: 24,
							right: 24,
						}}
					>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 700,
								fontSize: 22,
								color: "var(--color-dashboard-on-fill)",
								letterSpacing: "-0.02em",
							}}
						>
							{project.title}
						</h2>
						<p
							style={{
								marginTop: 4,
								fontSize: 13,
								color:
									"color-mix(in srgb, var(--color-dashboard-on-fill) 75%, transparent)",
								fontFamily: "var(--font-body)",
							}}
						>
							{project.shortDescription}
						</p>
					</div>
				</div>

				{/* Body */}
				<div
					style={{
						padding: "24px 28px",
						display: "flex",
						flexDirection: "column",
						gap: 20,
					}}
				>
					{data.previewOnly ? (
						<div
							style={{
								background: "var(--color-dashboard-divider)",
								borderRadius: 10,
								padding: "28px 24px",
								textAlign: "center",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 12,
							}}
						>
							<div
								style={{
									width: 48,
									height: 48,
									borderRadius: "50%",
									background: "var(--color-dashboard-dark)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Lock
									className="h-5 w-5"
									style={{ color: "var(--color-dashboard-on-fill)" }}
								/>
							</div>
							<p
								style={{
									fontWeight: 600,
									fontSize: 15,
									color: "var(--color-dashboard-text)",
									fontFamily: "var(--font-display)",
								}}
							>
								Sign up to view full project details.
							</p>
							<p
								style={{
									fontSize: 13,
									color: "var(--color-dashboard-muted)",
									fontFamily: "var(--font-body)",
								}}
							>
								Full gallery, detailed notes, category, and completion details
								are available after login.
							</p>
							<div
								style={{
									display: "flex",
									gap: 10,
									flexWrap: "wrap",
									justifyContent: "center",
								}}
							>
								<StudioButton
									variant="primary"
									size="sm"
									onClick={onRequireAuth}
								>
									Create Account
								</StudioButton>
								<StudioButton variant="quiet" size="sm" onClick={onRequireAuth}>
									Log in
								</StudioButton>
							</div>
						</div>
					) : (
						<>
							{/* Meta chips */}
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 1fr",
									gap: 8,
									background: "var(--color-dashboard-divider)",
									borderRadius: 7,
									padding: "12px 16px",
									fontSize: 13,
									color: "var(--color-dashboard-text)",
									fontFamily: "var(--font-body)",
								}}
							>
								<p>
									<span style={{ fontWeight: 600 }}>Category:</span>{" "}
									{project.category || "Not specified"}
								</p>
								<p>
									<span style={{ fontWeight: 600 }}>Completion:</span>{" "}
									{project.completionDate
										? new Date(project.completionDate).toLocaleDateString(
												"en-IN",
											)
										: "Not specified"}
								</p>
							</div>

							{project.fullDescription ? (
								<p
									style={{
										fontSize: 14,
										color: "var(--color-dashboard-muted-dark)",
										fontFamily: "var(--font-body)",
										lineHeight: 1.65,
									}}
								>
									{project.fullDescription}
								</p>
							) : null}

							{/* Image gallery */}
							<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
								{(project.imageUrls || []).map((url) => (
									<div key={url} className="overflow-hidden rounded-xl">
										<img
											src={resolveMediaUrl(url)}
											alt={project.title}
											className="aspect-square w-full object-cover transition-transform duration-300 hover:scale-110"
										/>
									</div>
								))}
							</div>
						</>
					)}

					<div style={{ display: "flex", justifyContent: "flex-end" }}>
						<StudioButton variant="quiet" size="sm" onClick={onClose}>
							Close
						</StudioButton>
					</div>
				</div>
			</div>
		</div>
	);
}

export function PublicPortfolioPage(): React.ReactElement {
	const { t } = useTranslation();
	const { slug = "" } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { isAuthenticated } = useAuthStore();

	const [loading, setLoading] = useState(true);
	const [portfolio, setPortfolio] = useState<PublicPortfolioResponse | null>(
		null,
	);
	const [selected, setSelected] =
		useState<PublicPortfolioProjectResponse | null>(null);
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
		null,
	);
	const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null);
	const [reviews, setReviews] = useState<TargetReviewsResponse>({
		summary: { averageRating: null, reviewCount: 0 },
		reviews: [],
	});
	const [reviewsFailed, setReviewsFailed] = useState(false);

	const requestedProjectId = useMemo(
		() => searchParams.get("project"),
		[searchParams],
	);

	useEffect(() => {
		async function load() {
			setLoading(true);
			setReviewsFailed(false);
			const response = await getPublicPortfolio(slug);
			setPortfolio(response);
			if (response) {
				try {
					const reviewResult = await fetchDesignerReviews(response.designer.id);
					setReviews(reviewResult);
				} catch (err) {
					console.error("Failed to fetch designer reviews:", err);
					setReviewsFailed(true);
				}
			}
			setLoading(false);
		}

		if (slug) {
			void load();
		}
	}, [slug]);

	useEffect(() => {
		async function loadProject(projectId: string) {
			setLoadingProjectId(projectId);
			const response = await getPublicPortfolioProject(slug, projectId);
			setLoadingProjectId(null);
			if (response) {
				setSelected(response);
				setSelectedProjectId(projectId);
			} else {
				setSelected(null);
				setSelectedProjectId(null);
				setSearchParams((prev) => {
					const next = new URLSearchParams(prev);
					next.delete("project");
					return next;
				});
			}
		}

		if (requestedProjectId && portfolio) {
			void loadProject(requestedProjectId);
		}
	}, [requestedProjectId, portfolio, setSearchParams, slug]);

	async function openProject(projectId: string) {
		setLoadingProjectId(projectId);
		const response = await getPublicPortfolioProject(slug, projectId);
		setLoadingProjectId(null);

		if (response) {
			setSelected(response);
			setSelectedProjectId(projectId);
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.set("project", projectId);
				return next;
			});
		} else {
			setSelected(null);
			setSelectedProjectId(null);
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.delete("project");
				return next;
			});
		}
	}

	function closeModal() {
		setSelected(null);
		setSelectedProjectId(null);
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.delete("project");
			return next;
		});
	}

	function requireAuth() {
		const projectId = selectedProjectId || requestedProjectId;
		const redirectTo = `/portfolio/${slug}${projectId ? `?project=${projectId}` : ""}`;
		navigate("/login", { state: { redirectTo } });
	}

	if (loading) {
		return (
			<div className="space-y-4 p-4 sm:p-6">
				<Skeleton className="h-40 w-full rounded-2xl" />
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					<Skeleton className="h-64 rounded-xl" />
					<Skeleton className="h-64 rounded-xl" />
					<Skeleton className="h-64 rounded-xl" />
				</div>
			</div>
		);
	}

	if (!portfolio) {
		return (
			<div className="min-h-screen bg-transparent p-4 sm:p-12 flex items-center justify-center">
				<div style={{ maxWidth: 520, width: "100%" }}>
					<FloatPanel elevation={1} padding={40}>
						<div className="text-center space-y-3">
							<h2
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 22,
									color: "var(--color-dashboard-text)",
								}}
							>
								Portfolio not found
							</h2>
							<p
								style={{
									fontSize: 14,
									color: "var(--color-dashboard-muted)",
									fontFamily: "var(--font-body)",
								}}
							>
								The link may be invalid or this portfolio is not published.
							</p>
						</div>
					</FloatPanel>
				</div>
			</div>
		);
	}

	// Authenticated designers or admins can preview arbitrary templates via ?template=
	// Unauthenticated public visitors always see the published template.
	const requestedTemplate = searchParams.get(
		"template",
	) as PortfolioTemplate | null;
	const activeTemplate =
		isAuthenticated() &&
		requestedTemplate &&
		PORTFOLIO_TEMPLATES.some((item) => item.id === requestedTemplate)
			? requestedTemplate
			: portfolio.portfolio.template;

	const templateDef =
		PORTFOLIO_TEMPLATES.find((item) => item.id === activeTemplate) ||
		PORTFOLIO_TEMPLATES[0];

	return (
		<div
			className={`${templateDef.pageWrapperClass} p-4 sm:p-6 transition-colors duration-300`}
		>
			<div className="space-y-8 max-w-7xl mx-auto">
				{renderPublicTemplateHeader(activeTemplate, {
					designer: portfolio.designer,
					templateName: templateDef.name,
					templateSummary: templateDef.changesSummary,
					signedIn: isAuthenticated(),
					projectCount: portfolio.projects.length,
				})}

				<ReviewSummarySection
					title="Client Reviews"
					summary={reviews.summary}
					reviews={reviews.reviews}
					emptyMessage="No homeowner reviews yet."
					loadFailed={
						reviewsFailed
							? {
									message: t("reviews.loadFailed"),
									retryLabel: t("reviews.retry"),
									onRetry: () => {
										setReviewsFailed(false);
										fetchDesignerReviews(portfolio.designer.id)
											.then(setReviews)
											.catch((err: unknown) => {
												console.error("Failed to fetch designer reviews:", err);
												setReviewsFailed(true);
											});
									},
								}
							: undefined
					}
				/>

				{renderPublicTemplateSection(
					activeTemplate,
					portfolio.projects,
					openProject,
					loadingProjectId,
				)}

				{selected ? (
					<ProjectDetailsModal
						data={selected}
						onClose={closeModal}
						onRequireAuth={requireAuth}
					/>
				) : null}
			</div>
		</div>
	);
}
